<script setup lang="ts">
import {
  type CurrencyCode,
  formatDate,
  formatMoney,
  formatMonthShort,
  type Locale,
  type LocalDate,
  type Money,
  moneyProportion,
  niceMoneyTicks,
  signOfMoney,
  zeroMoney,
} from '@taler/domain'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { ChartPeriod } from '../internal/cash-flow-series.ts'
import type { ReportBucket } from '../internal/report-range.ts'
import ChartLegend from '@/features/reporting/components/ChartLegend.vue'

/** `flows` draws income and expense side by side; `net` draws one signed bar per bucket. */
const props = defineProps<{
  periods: readonly ChartPeriod[]
  currency: CurrencyCode
  bucket: ReportBucket
  mode: 'flows' | 'net'
  legend?: boolean
}>()

const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as Locale)

const HEIGHT = 260
const PAD = { top: 12, right: 8, bottom: 28, left: 72 }
const MIN_WIDTH = 320

/** Drawn at the frame's own pixel width, so axis text keeps its size whatever the card width. */
const frame = ref<HTMLElement>()
const WIDTH = ref(720)
let observer: ResizeObserver | undefined

onMounted(() => {
  if (frame.value === undefined || typeof ResizeObserver === 'undefined') return
  observer = new ResizeObserver(([entry]) => {
    WIDTH.value = Math.max(MIN_WIDTH, Math.round(entry?.contentRect.width ?? WIDTH.value))
  })
  observer.observe(frame.value)
})
onBeforeUnmount(() => observer?.disconnect())

const PLOT_W = computed(() => WIDTH.value - PAD.left - PAD.right)
const PLOT_H = HEIGHT - PAD.top - PAD.bottom
/** Past this many buckets only every nth label is drawn, so the axis stays legible. */
const MAX_LABELS = 16

const zero = computed(() => zeroMoney(props.currency))

const scale = computed(() => {
  const series = props.periods.flatMap((period) =>
    props.mode === 'flows' ? [period.income, period.expense] : [period.net],
  )
  const high = series.reduce((max, value) => (value.units > max.units ? value : max), zero.value)
  const low = series.reduce((min, value) => (value.units < min.units ? value : min), zero.value)
  const ticks = niceMoneyTicks(low, high, 4)
  return { ticks, min: ticks[0] ?? zero.value, max: ticks[ticks.length - 1] ?? zero.value }
})

function yOf(value: Money): number {
  return PAD.top + (1 - moneyProportion(value, scale.value.min, scale.value.max)) * PLOT_H
}

const baselineY = computed(() => yOf(zero.value))
const groupWidth = computed(() => (props.periods.length === 0 ? 0 : PLOT_W.value / props.periods.length))
const barWidth = computed(() => Math.min(16, groupWidth.value * (props.mode === 'flows' ? 0.32 : 0.5)))

function barBetween(x: number, value: Money, tone: string, key: string) {
  const top = yOf(value)
  return {
    key,
    x,
    y: Math.min(top, baselineY.value),
    width: barWidth.value,
    height: Math.abs(baselineY.value - top),
    tone,
  }
}

const bars = computed(() =>
  props.periods.flatMap((period, index) => {
    const centre = PAD.left + index * groupWidth.value + groupWidth.value / 2
    if (props.mode === 'net') {
      const tone = signOfMoney(period.net) < 0 ? 'var(--color-negative)' : 'var(--color-positive)'
      return [barBetween(centre - barWidth.value / 2, period.net, tone, `${index}-net`)]
    }
    return [
      barBetween(centre - barWidth.value - 1, period.income, 'var(--color-income)', `${index}-income`),
      barBetween(centre + 1, period.expense, 'var(--color-expense)', `${index}-expense`),
    ]
  }),
)

function labelOf(start: LocalDate): string {
  if (props.bucket === 'MONTH') return formatMonthShort(start, currentLocale.value)
  if (props.bucket === 'YEAR') return formatDate(start, currentLocale.value, 'iso').slice(0, 4)
  return formatDate(start, currentLocale.value, 'iso').slice(5)
}

const labelStep = computed(() => Math.max(1, Math.ceil(props.periods.length / MAX_LABELS)))

const labels = computed(() =>
  props.periods
    .map((period, index) => ({
      key: period.start.epochDay,
      x: PAD.left + index * groupWidth.value + groupWidth.value / 2,
      text: labelOf(period.start),
      shown: index % labelStep.value === 0,
    }))
    .filter((label) => label.shown),
)

function tickText(value: Money): string {
  return formatMoney(value, { locale: currentLocale.value, showSymbol: false })
}
</script>

<template>
  <div ref="frame" class="flex min-w-0 flex-col gap-[var(--space-inline)]">
    <svg
      :viewBox="`0 0 ${WIDTH} ${HEIGHT}`"
      role="img"
      :aria-label="t(mode === 'flows' ? 'reporting.chart.flowsAria' : 'reporting.chart.netAria')"
      class="h-auto w-full"
    >
      <g v-for="tick in scale.ticks" :key="tick.units.toString()">
        <line
          :x1="PAD.left"
          :x2="WIDTH - PAD.right"
          :y1="yOf(tick)"
          :y2="yOf(tick)"
          stroke="var(--color-border)"
          stroke-width="1"
        />
        <text
          :x="PAD.left - 8"
          :y="yOf(tick) + 4"
          text-anchor="end"
          class="fill-[color:var(--color-text-muted)] text-[11px] tabular-nums"
        >
          {{ tickText(tick) }}
        </text>
      </g>
      <rect
        v-for="bar in bars"
        :key="bar.key"
        :x="bar.x"
        :y="bar.y"
        :width="bar.width"
        :height="bar.height"
        rx="3"
        :fill="bar.tone"
      />
      <text
        v-for="label in labels"
        :key="label.key"
        :x="label.x"
        :y="HEIGHT - 8"
        text-anchor="middle"
        class="fill-[color:var(--color-text-muted)] text-[11px]"
      >
        {{ label.text }}
      </text>
    </svg>
    <ChartLegend
      v-if="mode === 'flows' && legend !== false"
      :items="[
        { key: 'income', label: t('reporting.chart.income'), colour: 'var(--color-income)' },
        { key: 'expense', label: t('reporting.chart.expenses'), colour: 'var(--color-expense)' },
      ]"
    />
    <div class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
      <span v-if="periods.length === 0">{{ t('reporting.chart.empty') }}</span>
    </div>
  </div>
</template>
